import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stack,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import type { SocialLink } from "../../types/outreach";
import { OutreachCategory, genreOptions } from "../../types/outreach";
import { OutreachFormItem } from "../../hooks/useOutreachForm";
import TagAutocomplete from "../common/TagAutocomplete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { SocialLinksInput } from "./SocialLinksInput";
import {
  formatLegacySocialHandles,
  resolveSocialLinks,
} from "../../utils/socialLinks";

interface OutreachFormDialogProps {
  open: boolean;
  mode: "add" | "edit";
  category: OutreachCategory;
  currentItem: OutreachFormItem | null;
  onClose: () => void;
  onSubmit: () => void;
  onFieldChange: (field: string, value: any) => void;
  onApplyChannelAnalytics?: () => void;
  analyticsStatus?: "idle" | "loading" | "success" | "error";
  analyticsMessage?: string | null;
}

export const OutreachFormDialog: React.FC<OutreachFormDialogProps> = ({
  open,
  mode,
  category,
  currentItem,
  onClose,
  onSubmit,
  onFieldChange,
  onApplyChannelAnalytics,
  analyticsStatus = "idle",
  analyticsMessage,
}) => {
  const getCategoryDisplayName = (category: OutreachCategory): string => {
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
        return "Reaction Channel";
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return "Playlisting Channel";
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return "Spotify Playlist";
      case OutreachCategory.MUSIC_BLOGS:
        return "Music Blog";
      case OutreachCategory.CAMPAIGNS:
        return "Campaign";
      default:
        return "Item";
    }
  };

  const renderFormFields = () => {
    if (!currentItem) return null;

    const commonFields = (
      <Autocomplete
        multiple
        options={genreOptions}
        getOptionLabel={(option) => option}
        value={currentItem?.genre ? currentItem.genre.split("/") : []}
        onChange={(event, newValue) => {
          onFieldChange("genre", newValue.join("/"));
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Genre"
            fullWidth
            margin="normal"
            required
          />
        )}
      />
    );

    switch (category) {
      case OutreachCategory.REACTION_CHANNELS: {
        const resolvedSocialLinks = resolveSocialLinks(
          (currentItem?.socialLinks as SocialLink[]) ?? undefined,
          (currentItem?.socialHandles as string) ?? ""
        );

        const handleSocialLinksChange = (links: SocialLink[]) => {
          onFieldChange("socialLinks", links);
          onFieldChange("socialHandles", formatLegacySocialHandles(links));
        };

        return (
          <>
            <TextField
              label="Channel Name"
              fullWidth
              margin="normal"
              value={currentItem?.channelName || ""}
              onChange={(e) => onFieldChange("channelName", e.target.value)}
              required
            />
            <Stack spacing={1}>
              <TextField
                label="Channel URL"
                fullWidth
                margin="normal"
                value={currentItem?.channelUrl || ""}
                onChange={(e) => onFieldChange("channelUrl", e.target.value)}
                required
              />
              {onApplyChannelAnalytics && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    variant="outlined"
                    onClick={onApplyChannelAnalytics}
                    disabled={
                      !currentItem?.channelUrl || analyticsStatus === "loading"
                    }
                    startIcon={
                      analyticsStatus === "loading" ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : undefined
                    }
                  >
                    {analyticsStatus === "loading" ? "Applying..." : "Apply"}
                  </Button>
                  {analyticsStatus === "success" && (
                    <Typography
                      color="success.main"
                      display="flex"
                      alignItems="center"
                      variant="body2"
                      gap={0.5}
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                      {analyticsMessage || "Analytics applied"}
                    </Typography>
                  )}
                  {analyticsStatus === "error" && (
                    <Typography
                      color="error.main"
                      display="flex"
                      alignItems="center"
                      variant="body2"
                      gap={0.5}
                    >
                      <ErrorOutlineIcon fontSize="small" />
                      {analyticsMessage || "Failed to apply analytics"}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
            <TextField
              label="Subscriber Count"
              fullWidth
              margin="normal"
              value={currentItem?.subscriberCount || ""}
              onChange={(e) => onFieldChange("subscriberCount", e.target.value)}
            />
            {commonFields}
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={currentItem?.email || ""}
              onChange={(e) => onFieldChange("email", e.target.value)}
            />
            <SocialLinksInput
              value={resolvedSocialLinks}
              legacyValue={(currentItem?.socialHandles as string) ?? ""}
              onChange={handleSocialLinksChange}
              helperText="Add Instagram, X, YouTube handles or URLs"
            />
          </>
        );
      }
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return (
          <>
            <TextField
              label="Channel Name"
              fullWidth
              margin="normal"
              value={currentItem?.channelName || ""}
              onChange={(e) => onFieldChange("channelName", e.target.value)}
              required
            />
            <Stack spacing={1}>
              <TextField
                label="Channel URL"
                fullWidth
                margin="normal"
                value={currentItem?.channelUrl || ""}
                onChange={(e) => onFieldChange("channelUrl", e.target.value)}
                required
              />
              {onApplyChannelAnalytics && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    variant="outlined"
                    onClick={onApplyChannelAnalytics}
                    disabled={
                      !currentItem?.channelUrl || analyticsStatus === "loading"
                    }
                    startIcon={
                      analyticsStatus === "loading" ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : undefined
                    }
                  >
                    {analyticsStatus === "loading" ? "Applying..." : "Apply"}
                  </Button>
                  {analyticsStatus === "success" && (
                    <Typography
                      color="success.main"
                      display="flex"
                      alignItems="center"
                      variant="body2"
                      gap={0.5}
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                      {analyticsMessage || "Analytics applied"}
                    </Typography>
                  )}
                  {analyticsStatus === "error" && (
                    <Typography
                      color="error.main"
                      display="flex"
                      alignItems="center"
                      variant="body2"
                      gap={0.5}
                    >
                      <ErrorOutlineIcon fontSize="small" />
                      {analyticsMessage || "Failed to apply analytics"}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
            <TextField
              label="Subscriber Count"
              fullWidth
              margin="normal"
              value={currentItem?.subscriberCount || ""}
              onChange={(e) => onFieldChange("subscriberCount", e.target.value)}
            />
            {commonFields}
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={currentItem?.email || ""}
              onChange={(e) => onFieldChange("email", e.target.value)}
            />
          </>
        );
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return (
          <>
            <TextField
              label="Playlist Name"
              fullWidth
              margin="normal"
              value={currentItem?.playlistName || ""}
              onChange={(e) => onFieldChange("playlistName", e.target.value)}
              required
            />
            <TextField
              label="Playlist URL"
              fullWidth
              margin="normal"
              value={currentItem?.playlistUrl || ""}
              onChange={(e) => onFieldChange("playlistUrl", e.target.value)}
              required
            />
            <TextField
              label="Curator Social Handle"
              fullWidth
              margin="normal"
              value={currentItem?.curatorSocialHandle || ""}
              onChange={(e) =>
                onFieldChange("curatorSocialHandle", e.target.value)
              }
              helperText="Format: handle (IG), handle (TW)"
            />
            <TextField
              label="Source"
              fullWidth
              margin="normal"
              value={currentItem?.source || ""}
              onChange={(e) => onFieldChange("source", e.target.value)}
            />
            {commonFields}
            <TextField
              label="Curator Email"
              fullWidth
              margin="normal"
              value={currentItem?.curatorEmail || ""}
              onChange={(e) => onFieldChange("curatorEmail", e.target.value)}
            />
            <TextField
              label="Playlist Likes"
              type="number"
              fullWidth
              margin="normal"
              value={currentItem?.playlistLikes || 0}
              onChange={(e) =>
                onFieldChange("playlistLikes", parseInt(e.target.value) || 0)
              }
            />
            <TextField
              label="Playlist Followers"
              type="number"
              fullWidth
              margin="normal"
              value={currentItem?.playlistFollowers || 0}
              onChange={(e) =>
                onFieldChange(
                  "playlistFollowers",
                  parseInt(e.target.value) || 0
                )
              }
            />
          </>
        );
      case OutreachCategory.MUSIC_BLOGS:
        return (
          <>
            <TextField
              label="Blog Name"
              fullWidth
              margin="normal"
              value={currentItem?.blogName || ""}
              onChange={(e) => onFieldChange("blogName", e.target.value)}
              required
            />
            <TextField
              label="Contact"
              fullWidth
              margin="normal"
              value={currentItem?.contact || ""}
              onChange={(e) => onFieldChange("contact", e.target.value)}
            />
            <TextField
              label="Blog URL"
              fullWidth
              margin="normal"
              value={currentItem?.blogUrl || ""}
              onChange={(e) => onFieldChange("blogUrl", e.target.value)}
              required
            />
            {commonFields}
            <TextField
              label="Monthly Visits"
              type="number"
              fullWidth
              margin="normal"
              value={currentItem?.monthlyVisits || 0}
              onChange={(e) =>
                onFieldChange("monthlyVisits", parseInt(e.target.value) || 0)
              }
            />
          </>
        );
      case OutreachCategory.CAMPAIGNS:
        return (
          <>
            <TextField
              label="Campaign Name"
              fullWidth
              margin="normal"
              value={currentItem?.name || ""}
              onChange={(e) => onFieldChange("name", e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={currentItem?.description || ""}
              onChange={(e) => onFieldChange("description", e.target.value)}
            />
            {commonFields}
            <TagAutocomplete
              value={currentItem?.tag ?? null}
              category={OutreachCategory.CAMPAIGNS}
              includeGlobal
              onChange={(tagValue, tag) => {
                onFieldChange("tag", tagValue);
                onFieldChange("tagId", tag?.id ?? null);
              }}
              label="Tag"
              placeholder="Select or create a campaign tag"
              textFieldProps={{
                fullWidth: true,
                margin: "normal",
              }}
            />
            {/* Hidden fields with default values */}
            <input
              type="hidden"
              value={currentItem?.createdAt || new Date().toISOString()}
              onChange={(e) => onFieldChange("createdAt", e.target.value)}
            />
            <input
              type="hidden"
              value={new Date().toISOString()}
              onChange={(e) => onFieldChange("updatedAt", e.target.value)}
            />
            <input
              type="hidden"
              value={currentItem?.saved || false}
              onChange={(e) => onFieldChange("saved", e.target.value)}
            />
            <input
              type="hidden"
              value={currentItem?.recipients || []}
              onChange={(e) => onFieldChange("recipients", e.target.value)}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "add"
          ? `Add ${getCategoryDisplayName(category)}`
          : `Edit ${getCategoryDisplayName(category)}`}
      </DialogTitle>
      <DialogContent>{renderFormFields()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">
          {mode === "add" ? "Add" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
